import cx from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@components/card/Card';
import { Icon } from '@components/icon/Icon';
import { IconButton } from '@components/iconButton/IconButton';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { parseToShortDateFormat } from '@shared/utils/dateFormatters';
import { formatWeekdayList } from '@shared/utils/weekDayFormatters';
import { OPERATIONS_BULLETIN_BOARD_UPDATE_PATH } from 'restaurantAdmin/paths';
import typography from '~styles/typography.scss';
import { useRestaurant } from '../../context/useRestaurant';
import type { RestaurantBulletin } from './apiHelpers';
import { DeleteRestaurantBulletinConfirmationModal } from './DeleteRestaurantBulletinConfirmationModal';
import styles from './RestaurantBulletinsList.scss';

interface RestaurantBulletinsTableProps {
  refetchRestaurantBulletins: () => void;
  restaurantBulletins: RestaurantBulletin[];
}

export const RestaurantBulletinsList = ({
  refetchRestaurantBulletins,
  restaurantBulletins,
}: RestaurantBulletinsTableProps) => {
  const { close, isOpen, open } = useIsOpen();
  const { id: restaurantId } = useRestaurant();
  const [bulletinToDelete, setBulletinToDelete] =
    useState<RestaurantBulletin>();

  return (
    <>
      <ol aria-label="Restaurant Bulletins" className={styles.bulletinList}>
        {restaurantBulletins.map((restaurantBulletin) => {
          const { description, endDate, id, name, repeat, startDate } =
            restaurantBulletin;
          const shortStartDate = parseToShortDateFormat(startDate);
          const shortEndDate = endDate
            ? parseToShortDateFormat(endDate)
            : undefined;
          const oneDayBulletin = shortStartDate === shortEndDate;

          return (
            <li className={styles.bulletinItem} key={id}>
              <Card>
                <header className={styles.cardHeader}>
                  <h2 className={typography.h5}>{name}</h2>
                  <div>
                    <Link
                      className={cx(typography.c2, styles.editLink)}
                      state={{ restaurantBulletin }}
                      title={`Edit ${name}`}
                      to={OPERATIONS_BULLETIN_BOARD_UPDATE_PATH}
                    >
                      <Icon ariaLabel={`Edit ${name}`} name="pencil" />
                    </Link>
                    <IconButton
                      ariaLabel={`Delete ${name}`}
                      className={styles.deleteButton}
                      iconName="trash"
                      onClick={() => {
                        setBulletinToDelete(restaurantBulletin);
                        open();
                      }}
                    />
                  </div>
                </header>
                <p className={cx(typography.c2, styles.bulletinContent)}>
                  {description}
                </p>
                <h3 className={cx(typography.h6, styles.bulletinSubheading)}>
                  {oneDayBulletin ? 'Date' : 'Start and end dates'}
                </h3>
                <p className={cx(typography.c2, styles.bulletinContent)}>
                  {oneDayBulletin
                    ? shortStartDate
                    : `${shortStartDate}–${shortEndDate || 'Never'}`}
                </p>
                {Boolean(repeat.length) && (
                  <>
                    <h3
                      className={cx(typography.h6, styles.bulletinSubheading)}
                    >
                      Repeats
                    </h3>
                    <p className={cx(typography.c2, styles.bulletinContent)}>
                      {formatWeekdayList(repeat)}
                    </p>
                  </>
                )}
              </Card>
            </li>
          );
        })}
      </ol>
      {bulletinToDelete && (
        <DeleteRestaurantBulletinConfirmationModal
          closeModal={close}
          bulletin={bulletinToDelete}
          isOpen={isOpen}
          refetchRestaurantBulletins={refetchRestaurantBulletins}
          restaurantId={restaurantId}
          setBulletinToDelete={setBulletinToDelete}
        />
      )}
    </>
  );
};
